import { useCallback, useEffect, useMemo, useState } from 'react';
import { DecorativeIcon } from './DecorativeIcon';
import { CollabSearchInput } from '@nimbalyst/collab-bundle/docs-ui';
import {
  grantProjectAccess,
  listProjectAccess,
  revokeProjectAccess,
  type ProjectAccessGrant,
  type TeamMember,
  type TeamSummary,
} from '../api';
import type { TeamJwt } from '../auth/teamJwt';
import {
  CARD,
  FIELD,
  SECTION_SUBTITLE,
  SECTION_TITLE,
} from '../controlStyles';
import {
  PROJECT_ROLES,
  PROJECT_ROLE_LABELS,
  buildProjectAccessRows,
  memberIdentity,
  projectDisplayName,
  searchMembers,
  sortMembers,
  DEFAULT_MEMBER_SORT,
  type ProjectRole,
} from './memberDirectory';

type TeamJwtProvider = () => Promise<TeamJwt>;

/**
 * Per-project access, against the grants the TeamRoom has stored since Epic H1
 * (`/api/teams/{orgId}/project-access`). The console listed org roles and
 * stopped there, so an admin could see that someone was a member and had no way
 * to see, let alone change, which projects that membership actually reached.
 *
 * Every row is rendered from the org roster rather than from the grant list, so
 * a member with no grant is a row you can grant from instead of an absence, and
 * a stale grant for a departed member never renders as a bare member id.
 */
export function ProjectAccessPanel({
  getTeamJwt,
  team,
  members,
  disabled = false,
}: {
  getTeamJwt: TeamJwtProvider;
  team: TeamSummary;
  members: readonly TeamMember[];
  disabled?: boolean;
}) {
  const [projectId, setProjectId] = useState(team.projects[0]?.projectId ?? '');
  const [grants, setGrants] = useState<ProjectAccessGrant[] | null>(null);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!projectId) return;
    const jwt = await getTeamJwt();
    const next = await listProjectAccess(jwt, team.orgId, projectId, signal);
    if (!signal?.aborted) setGrants(next);
  }, [getTeamJwt, projectId, team.orgId]);

  useEffect(() => {
    const controller = new AbortController();
    setGrants(null);
    setError(null);
    load(controller.signal).catch((cause) => {
      if (!controller.signal.aborted) {
        setError(cause instanceof Error ? cause.message : 'Unable to load project access.');
      }
    });
    return () => controller.abort();
  }, [load]);

  const rows = useMemo(() => {
    const visible = sortMembers(searchMembers(members, search), DEFAULT_MEMBER_SORT);
    return buildProjectAccessRows(visible, grants ?? []);
  }, [grants, members, search]);

  const change = async (member: TeamMember, next: ProjectRole | null) => {
    setBusy(member.memberId);
    setError(null);
    try {
      const jwt = await getTeamJwt();
      if (next === null) await revokeProjectAccess(jwt, team.orgId, projectId, member.memberId);
      else await grantProjectAccess(jwt, team.orgId, projectId, member.memberId, next);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Project access update failed.');
    } finally {
      setBusy(null);
    }
  };

  if (!projectId) return null;
  const grantedCount = rows.filter((row) => row.projectRole !== null).length;

  return (
    <section className={`project-access-card ${CARD}`}>
      <header className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <h3 className={SECTION_TITLE}>Project access</h3>
          <p className={SECTION_SUBTITLE}>
            {grantedCount} of {rows.length} {rows.length === 1 ? 'member has' : 'members have'} an explicit grant on
            this project.
          </p>
        </div>
        {team.projects.length > 1 && (
          <label className="flex items-center gap-2 text-[0.72rem] text-nim-muted">
            <DecorativeIcon icon="folder" size={16} />
            <select
              className={`project-access-project ${FIELD} w-[14rem]`}
              aria-label="Project"
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
            >
              {team.projects.map((project) => (
                <option key={project.projectId} value={project.projectId}>
                  {projectDisplayName(project)}
                </option>
              ))}
            </select>
          </label>
        )}
        <CollabSearchInput
          className="w-full basis-full [@container_organization-members_(min-width:44rem)]:w-56 [@container_organization-members_(min-width:44rem)]:basis-auto"
          value={search}
          onChange={setSearch}
          placeholder="Search members..."
          label="Search members for project access"
        />
      </header>

      {error && (
        <p className="mt-3 mb-0 text-[0.78rem] text-[var(--nim-error)]" role="alert">{error}</p>
      )}

      {grants === null ? (
        <p className="m-0 py-6 text-center text-[0.82rem] text-nim-muted">Loading project access…</p>
      ) : (
        <div className="project-access-list mt-4 border-t border-nim">
          {rows.length === 0 ? (
            <p className="m-0 py-6 text-center text-[0.82rem] text-nim-muted">No members match that search.</p>
          ) : rows.map(({ member, projectRole }) => {
            const identity = memberIdentity(member);
            return (
              <div
                className="project-access-row grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-nim py-2.5"
                key={member.memberId}
              >
                <div className="min-w-0">
                  <strong className="block truncate text-[0.84rem] font-medium text-nim">{identity.title}</strong>
                  {identity.subtitle && (
                    <span className="block truncate text-[0.72rem] text-nim-muted">{identity.subtitle}</span>
                  )}
                </div>
                <select
                  className={`project-access-role ${FIELD} w-[9.5rem]`}
                  aria-label={`Project access for ${identity.title}`}
                  value={projectRole ?? ''}
                  disabled={disabled || busy !== null}
                  onChange={(event) => void change(
                    member,
                    event.target.value === '' ? null : (event.target.value as ProjectRole),
                  )}
                >
                  <option value="">No access</option>
                  {PROJECT_ROLES.map((role) => (
                    <option key={role} value={role}>{PROJECT_ROLE_LABELS[role]}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
